"use client";
import { useIsMobile } from "@/hooks/useIsMobile";
import { useTranslationDetector } from "@/hooks/useTranslationDetector";
import {
  dateFormats,
  formatDate,
  getShortDayNames,
} from "@/utils/dateFormatter";
import { byPrefixAndName } from "@awesome.me/kit-12cf79935c/icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import clsx from "clsx";
import { format } from "date-fns";
import { useLocale } from "next-intl";
import { memo, useCallback, useEffect, useMemo, useState } from "react";
import { DayPicker, getDefaultClassNames } from "react-day-picker";
import { getDayPickerLocale } from "@/utils/datePickerUtils";
import { getWeekStartsOn } from "@/utils/datePickerUtils";
import PriceDayButton from "./PriceDayButton";

const DatePickerCalendarContent = memo(
  ({
    value,
    handleDateSelect,
    defaultMonth,
    getPriceByDate,
    isOneOfCheapestDay,
    currency,
    isRoundTrip,
    mobileDepartureClick,
    isFromDate,
    departureDate,
    mobileReturnClick,
    returnDate,
    isMultiCity = false,
    publicHolidays,
    translationKey,
  }) => {
    const isMobile = useIsMobile();
    const locale = useLocale();
    const t = useTranslationDetector(translationKey);
    const defaultClassNames = getDefaultClassNames();

    const dayPickerLocale = useMemo(
      () => getDayPickerLocale(locale),
      [locale],
    );
    const weekStartsOn = useMemo(
      () => getWeekStartsOn(locale),
      [locale],
    );

    // Hover range preview state
    const [hoveredDate, setHoveredDate] = useState(null);

    const defaultMonthValue = useMemo(() => {
      if (isMobile) {
        const now = new Date();
        return new Date(now.getFullYear(), now.getMonth());
      } else {
        const date = new Date(defaultMonth);
        return new Date(date.getFullYear(), date.getMonth());
      }
    }, [isMobile, defaultMonth]);

    const memoizedPriceDayButton = useCallback(
      (props) => (
        <PriceDayButton
          {...props}
          weekStartsOn={weekStartsOn}
          showReturnToolTip={!returnDate ? t("addReturnToolTip") : false}
          getPriceByDate={getPriceByDate}
          isOneOfCheapestDay={isOneOfCheapestDay}
          currency={currency}
          isHoliday={publicHolidays.some(
            (holiday) =>
              holiday.date ===
              formatDate(props.day.date, { format: "YYYY-MM-DD" }),
          )}
        />
      ),
      [
        getPriceByDate,
        currency,
        publicHolidays,
        returnDate,
        t,
        isOneOfCheapestDay,
        weekStartsOn,
      ],
    );

    // Mouse hover handlers for range preview - debounced
    const handleDayMouseEnter = useCallback(
      (date) => {
        // Range seçimi sırasında veya gidiş-dönüş aynı tarihte hover işlemi yap
        const isSameDate =
          value?.from &&
          value?.to &&
          value.from.getTime() === value.to.getTime();

        if (
          value?.from &&
          (!value?.to || isSameDate) &&
          date &&
          date > value.from
        ) {
          setHoveredDate(date);
        } else if (!value?.from || (value?.to && !isSameDate)) {
          // Range seçimi yoksa veya farklı tarihler seçilmişse hover'ı temizle
          setHoveredDate(null);
        }
      },
      [value?.from, value?.to],
    );

    const handleDayMouseLeave = useCallback(() => {
      // Sadece hover varsa temizle
      setHoveredDate(null);
    }, []);

    // Create hover range preview modifiers with useMemo for performance
    const modifiers = useMemo(() => {
      const isSameDate =
        value?.from && value?.to && value.from.getTime() === value.to.getTime();

      if (
        !value?.from ||
        !hoveredDate ||
        (value?.to && !isSameDate) ||
        hoveredDate <= value.from
      ) {
        return {};
      }

      // Başlangıçtan sonraki günden hover edilen güne kadar (dahil)
      const nextDay = new Date(value.from);
      nextDay.setDate(nextDay.getDate() + 1);

      // Hover edilen gün dahil
      const result = {
        hoverRange: {
          from: nextDay,
          to: hoveredDate, // Hover edilen gün dahil
        },
      };

      return result;
    }, [value?.from, value?.to, hoveredDate]);

    useEffect(() => {
      setTimeout(() => {
        const container = document.querySelector(".months-container");
        const selectedMonth = container?.querySelector(".selected-month");

        if (container && selectedMonth) {
          const selectedTop = selectedMonth.offsetTop;
          const containerHeight = container.clientHeight;
          const selectedHeight = selectedMonth.clientHeight;

          // Seçili elemanı container'ın ortasına getir
          const scrollTo =
            selectedTop - containerHeight / 2 + selectedHeight / 2;

          container.scrollTop = scrollTo;
        }
      }, 100);
    }, []);

    return (
      <div className="flex h-full flex-col gap-4 px-0 py-4 md:px-4">
        {!isMultiCity && (
          <div className="flex items-center justify-between gap-4 px-4 md:hidden md:px-0">
            <button
              // onClick={mobileDepartureClick}
              className={clsx(
                "bg-primary-weaker flex h-14 w-1/2 flex-col items-start justify-center rounded-lg border-2 px-3 py-2",
                !departureDate
                  ? "border-primary bg-primary-weaker"
                  : "border-border bg-white",
              )}
            >
              <span className="text-xs">{t("departure")}</span>
              <span className="text-sm font-medium">
                {dateFormats.dayLongMonthShortDay(departureDate, locale)}
              </span>
            </button>
            <button
              // onClick={mobileReturnClick}
              className={clsx(
                "flex h-14 w-1/2 flex-col justify-center rounded-lg border-2 px-3 py-2",
                !returnDate
                  ? "border-primary bg-primary-weaker items-center"
                  : "border-border items-start bg-white",
              )}
            >
              {returnDate ? (
                <>
                  <span className="text-xs">{t("return")}</span>
                  <span className="text-sm font-medium">
                    {returnDate
                      ? dateFormats.dayLongMonthShortDay(returnDate, locale)
                      : ""}
                  </span>
                </>
              ) : (
                <span className="text-center text-sm/19 font-medium">
                  {t("addReturnPlaceholder")}
                </span>
              )}
            </button>
          </div>
        )}

        <div className="border-primary-weak flex items-center justify-between border-b px-2 md:hidden">
          {getShortDayNames(locale).map((day) => (
            <span className="w-[55px] py-2 text-center" key={day}>
              {day}
            </span>
          ))}
        </div>

        <div className="months-container h-full overflow-scroll px-0 pb-4 md:overflow-visible md:pb-0">
          <DayPicker
            formatters={{
              formatWeekdayName: (date, options) =>
                format(date, "ccc", options),
            }}
            key={`daypicker-${isMobile ? "mobile" : "desktop"}`}
            locale={dayPickerLocale}
            animate
            mode="range"
            selected={value}
            onSelect={handleDateSelect}
            // onDayMouseEnter={!isMobile ? handleDayMouseEnter : undefined}
            // onDayMouseLeave={!isMobile ? handleDayMouseLeave : undefined}
            // modifiers={!isMobile ? modifiers : {}}
            // modifiersStyles={{
            //   hoverRange: {
            //     backgroundColor: "#F2F2F9",
            //   },
            // }}
            numberOfMonths={isMobile ? 12 : 2}
            navLayout="around"
            hideWeekdays={isMobile ? true : false}
            // hideNavigation={isMobile ? true : false}
            defaultMonth={defaultMonthValue}
            disabled={{ before: new Date() }}
            classNames={{
              months: `${defaultClassNames.months} justify-between !max-w-full !gap-2`,
              month: `${defaultClassNames.month} w-full md:w-auto`,
              month_grid: `${defaultClassNames.month_grid} w-full`,
              month_caption: `${defaultClassNames.month_caption} h-auto md:pb-0`,
              disabled: "text-foreground-weaker-subtext",
              today: "text-primary",
              day: "font-semibold w-[55px] h-[58px]",
              selected: "",
              weekday:
                "border-b border-primary-weak text-sm text-foreground-muted font-medium h-11",
              button_next:
                "absolute right-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
              button_previous:
                "absolute left-0 top-[13px] w-[9px] text-black cursor-pointer opacity-0 md:opacity-100",
            }}
            components={{
              DayButton: memoizedPriceDayButton,
              Month: (props) => {
                const currentMonthHolidays = publicHolidays.filter(
                  (holiday) => {
                    const holidayDate = new Date(holiday.date);
                    const calendarDate = props.calendarMonth.date;

                    return (
                      holidayDate.getFullYear() ===
                        calendarDate.getFullYear() &&
                      holidayDate.getMonth() === calendarDate.getMonth()
                    );
                  },
                );

                return (
                  <div
                    className={clsx(
                      "flex w-full flex-col items-center justify-between gap-2 md:w-auto",
                      props.calendarMonth.date.getFullYear() ===
                        departureDate.getFullYear() &&
                        props.calendarMonth.date.getMonth() ===
                          departureDate.getMonth() &&
                        "selected-month",
                    )}
                  >
                    <div className="w-full">{props.children}</div>
                    {currentMonthHolidays?.length > 0 && (
                      <div className="flex w-full flex-col items-start gap-1 pl-[14px] md:pb-0">
                        {currentMonthHolidays.map((holiday) => (
                          <div
                            className={clsx(
                              "flex w-full items-center justify-start gap-[9px]",
                              !holiday.title && "hidden",
                            )}
                            key={holiday.date}
                          >
                            <span className="bg-tertiary-emphasis h-[6px] w-[6px] rounded-full"></span>
                            <span className="text-foreground-muted inline-flex items-center gap-1 text-sm">
                              <span>
                                {dateFormats.dayShortMonth(
                                  holiday.date,
                                  locale,
                                )}
                              </span>
                              <span>{holiday.title}</span>
                            </span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              },
              NextMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-right"]}
                    {...props}
                  />
                );
              },
              PreviousMonthButton: (props) => {
                return (
                  <FontAwesomeIcon
                    icon={byPrefixAndName.far["chevron-left"]}
                    {...props}
                  />
                );
              },
            }}
          />
        </div>
      </div>
    );
  },
);

DatePickerCalendarContent.displayName = "DatePickerCalendarContent";

export default DatePickerCalendarContent;
